import { useState, useCallback } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { DestinationForm } from "./components/destination-form"
import { DestinationsQuery } from "@/lib/graphql/queries"
import { DeleteDestinationMutation, BulkDeleteDestinationsMutation } from "@/lib/graphql/mutations"
import { DestinationChangedSubscription } from "@/lib/graphql/subscriptions"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"
import { Badge } from "@/components/ui/badge"
import { destinationTypeLabels, type DestinationTypeValue } from "@/lib/schemas"
import type { DestinationStatus } from "@/lib/generated/graphql"

type DestItem = ResultOf<typeof DestinationsQuery>["destinations"]["nodes"][number]

const LIMIT = 25

export function DestinationsPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const status = (searchParams.get("status") as DestinationStatus) || undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [status, search])

  const [result, reexecute] = useQuery({
    query: DestinationsQuery,
    variables: { status, search, after: pagination.after, first: pagination.first },
  })

  const handleDestChanged = useCallback(
    (
      _prev: ResultOf<typeof DestinationChangedSubscription> | undefined,
      response: ResultOf<typeof DestinationChangedSubscription>,
    ) => {
      if (response?.destinationChanged) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: DestinationChangedSubscription, pause: !isConfigured }, handleDestChanged)

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }
  const [, deleteDest] = useMutation(DeleteDestinationMutation)
  const [, bulkDeleteDests] = useMutation(BulkDeleteDestinationsMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const columns: Column<DestItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Type",
      cell: (row) => (
        <Badge variant={row.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
          {destinationTypeLabels[row.type as DestinationTypeValue] ?? row.type}
        </Badge>
      ),
    },
    {
      header: "URL",
      cell: (row) => (
        <code className="text-xs font-mono text-muted-foreground max-w-75 truncate block">
          {row.type === "HTTP" ? row.url : "—"}
        </code>
      ),
    },
    {
      header: "Health",
      cell: (row) => {
        if (row.disabledReason) {
          return (
            <Badge variant="destructive" className="text-2xs">
              {row.disabledReason === "unhealthy" ? "Auto-disabled: unhealthy" : `Disabled: ${row.disabledReason}`}
            </Badge>
          )
        }
        const score = row.healthScore
        const color = score >= 0.8 ? "bg-emerald-500" : score >= 0.4 ? "bg-amber-500" : "bg-red-500"
        return (
          <div className="flex items-center gap-1.5">
            <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
            <span className="text-xs tabular-nums">{Math.round(score * 100)}%</span>
          </div>
        )
      },
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Timeout",
      cell: (row) => <span className="text-xs tabular-nums">{row.timeoutMs}ms</span>,
    },
    {
      header: "Rate Limit",
      cell: (row) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {row.rateLimitRps > 0 ? `${row.rateLimitRps} rps` : "unlimited"}
        </span>
      ),
    },
    {
      header: "Auth",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {row.authType === "NONE" ? "—" : row.authType.toLowerCase()}
        </span>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteDest({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Destination deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteDests({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteDestinations ?? 0} destinations`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Destinations" description="Webhook delivery targets">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Destination
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search destinations..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.destinations.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.destinations.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No destinations"
        emptyDescription="Create a destination to define where webhooks are forwarded"
        onRowClick={(row) => navigate(`/destinations/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <DestinationForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Destination"
        description="This will permanently delete this destination."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Destinations"
        description={`This will permanently delete ${selectedKeys.size} selected destinations.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
